import type { SaturationMode, TrackState } from '@melodinai/types';
import { useMixerStore } from '../../stores/mixerStore';
import { clsx } from '../../lib/utils';

const MODES: { id: SaturationMode; label: string }[] = [
  { id: 'clean', label: 'Clean' },
  { id: 'analog', label: 'Analog' },
  { id: 'tape', label: 'Tape' },
  { id: 'tube', label: 'Tube' },
];

interface SaturationSectionProps {
  track: TrackState;
}

export function SaturationSection({ track }: SaturationSectionProps) {
  const setFxParam = useMixerStore((s) => s.setFxParam);
  const setSaturationMode = useMixerStore((s) => s.setSaturationMode);
  const sat = track.fxChain?.find((f) => f.type === 'Saturator');
  const p = (sat?.params || {}) as Record<string, number | string>;
  const mode = (p._sslMode as SaturationMode) || 'analog';

  return (
    <section className="space-y-2 border-b border-ssl-metal/50 pb-3">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ssl-accent">Saturation</h3>
      <div className="flex flex-wrap gap-1">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setSaturationMode(track.id, m.id)}
            className={clsx(
              'rounded border px-1.5 py-0.5 text-[9px]',
              mode === m.id
                ? 'border-ssl-accent bg-ssl-accent/15 text-ssl-accent'
                : 'border-ssl-metal/50 text-ssl-text hover:border-ssl-accent',
            )}
          >
            {m.label}
          </button>
        ))}
      </div>
      <div className="space-y-1.5 rounded border border-ssl-metal/40 bg-black/20 p-2">
        <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
          <span className="w-10">Drive</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={Number(p.drive ?? 0.45)}
            onChange={(e) => setFxParam(track.id, 'Saturator', 'drive', Number(e.target.value))}
            className="h-1 flex-1 accent-ssl-accent"
          />
          <span className="w-8 tabular-nums text-right">{Math.round(Number(p.drive ?? 0.45) * 100)}</span>
        </label>
        <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
          <span className="w-10">Mix</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={Number(p.wet ?? 0.55)}
            onChange={(e) => setFxParam(track.id, 'Saturator', 'wet', Number(e.target.value))}
            className="h-1 flex-1 accent-ssl-led"
          />
          <span className="w-8 tabular-nums text-right">{Math.round(Number(p.wet ?? 0.55) * 100)}</span>
        </label>
      </div>
    </section>
  );
}
